
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SaleValidationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  expectedAmount: number;
  onValidate: () => void;
  defaultDate: Date;
}

export const SaleValidationDialog = ({
  isOpen,
  onClose,
  expectedAmount,
  onValidate,
  defaultDate
}: SaleValidationDialogProps) => {
  const [countedAmount, setCountedAmount] = useState("");
  const [explanation, setExplanation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleValidate = async () => {
    if (!countedAmount) {
      toast.error("Veuillez saisir le montant compté");
      return;
    }

    const counted = parseFloat(countedAmount);
    const difference = counted - expectedAmount;
    
    if (difference !== 0 && !explanation) {
      toast.error("Une explication est requise en cas de différence");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Format date as YYYY-MM-DD
      const formattedDate = defaultDate.toISOString().split('T')[0];

      const { error } = await supabase
        .from("sales_validations")
        .insert({
          sale_date: formattedDate,
          expected_amount: expectedAmount,
          counted_amount: counted,
          difference_amount: difference,
          explanation: difference !== 0 ? explanation : null,
          created_by: user.id
        });

      if (error) throw error;

      toast.success("Validation enregistrée avec succès");
      onValidate();
      onClose();
    } catch (error: any) {
      console.error("Erreur lors de la validation:", error);
      toast.error(error.message || "Erreur lors de la validation");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Validation du montant</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Montant attendu
            </label>
            <div className="text-lg font-semibold">
              {expectedAmount.toLocaleString()} FCFA
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Montant compté
            </label>
            <Input
              type="number"
              value={countedAmount}
              onChange={(e) => setCountedAmount(e.target.value)}
              placeholder="Saisissez le montant compté"
            />
          </div>
          {countedAmount && parseFloat(countedAmount) !== expectedAmount && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Explication de la différence
              </label>
              <Textarea
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder="Expliquez la raison de la différence"
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleValidate} disabled={isSubmitting}>
            {isSubmitting ? "Validation..." : "Valider"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
